import { onScopeDispose, ref } from "vue";
import {
  EMOJI_PICKER_RECENT_LIMIT,
  EMOJI_PICKER_RECENT_STORAGE_KEY,
} from "@/src/design_system/button_and_controls/emoji_picker/constants/recent";
import { EMOJI_SKIN_TONE } from "@/src/shared/emoji/constants";
import { applyEmojiSkinTone, findEmojiByUnicode } from "@/src/shared/emoji/utils";

const normalizeRecentEmojis = (unicodes: unknown): string[] => {
  if (!Array.isArray(unicodes)) return [];

  const normalizedEmojis: string[] = [];
  const seen = new Set<string>();

  for (const unicode of unicodes) {
    const emoji = findEmojiByUnicode(unicode);
    if (!emoji) continue;

    const neutralUnicode = applyEmojiSkinTone(emoji, EMOJI_SKIN_TONE.Neutral).unicode;
    if (seen.has(neutralUnicode)) continue;

    normalizedEmojis.push(neutralUnicode);
    seen.add(neutralUnicode);
    if (normalizedEmojis.length === EMOJI_PICKER_RECENT_LIMIT) break;
  }

  return normalizedEmojis;
};

let activeConsumers = 0;
let pendingEmojis: string[] = [];

const readRecentEmojis = (): string[] | null => {
  let storedEmojis: string | null;

  try {
    storedEmojis = window.localStorage.getItem(EMOJI_PICKER_RECENT_STORAGE_KEY);
  } catch {
    return null;
  }

  if (!storedEmojis) return [];

  try {
    return normalizeRecentEmojis(JSON.parse(storedEmojis));
  } catch {
    return [];
  }
};

export const useEmojiPickerRecentHistory = () => {
  activeConsumers += 1;
  onScopeDispose(() => {
    activeConsumers -= 1;
    if (activeConsumers === 0) pendingEmojis = [];
  });

  const recentEmojis = ref<string[]>([]);

  const refreshRecentEmojis = (): void => {
    const persistedEmojis = readRecentEmojis();
    if (persistedEmojis === null) return;

    recentEmojis.value = normalizeRecentEmojis([...pendingEmojis, ...persistedEmojis]);
  };

  const rememberEmoji = (unicode: string): void => {
    const selectedEmoji = normalizeRecentEmojis([unicode])[0];
    if (!selectedEmoji) return;

    const persistedEmojis = readRecentEmojis() ?? [];
    recentEmojis.value = normalizeRecentEmojis([
      selectedEmoji,
      ...pendingEmojis,
      ...persistedEmojis,
      ...recentEmojis.value,
    ]);

    try {
      window.localStorage.setItem(EMOJI_PICKER_RECENT_STORAGE_KEY, JSON.stringify(recentEmojis.value));
      pendingEmojis = [];
    } catch {
      pendingEmojis = normalizeRecentEmojis([selectedEmoji, ...pendingEmojis]);
    }
  };

  return { recentEmojis, refreshRecentEmojis, rememberEmoji };
};
